import { useCompanyDataServiceV1 } from "@rw1/api-services";
import { CompanyData_V1 } from "@rw1/auto-generated-clients";
import { useMemo } from "react";
import { COMPANIES_QUERY_KEY, GC_TIME_MS } from "./constants";
import { useFetchAll } from "./useFetchAll";

interface UseCompaniesDataOptions {
  enabled?: boolean;
  pageNumber?: number;
  pageSize?: number;
  companyNames?: string[];
  ssoDomain?: string;
  includeBinaries?: boolean;
}

interface UseCompaniesDataResult {
  data: CompanyData_V1.Company[] | undefined;
  companiesData: CompanyData_V1.Company[] | undefined;
  isLoading: boolean;
  isFetching: boolean;
  companiesLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  companyNamesByIdMap: Record<string, string>;
}

export const useCompaniesData = (
  options: UseCompaniesDataOptions = {},
): UseCompaniesDataResult => {
  const {
    enabled = true,
    pageSize = 1000,
    companyNames,
    ssoDomain,
    includeBinaries,
  } = options;

  const companyDataService = useCompanyDataServiceV1();

  const {
    data: companiesData,
    isLoading: companiesLoading,
    isFetching,
    error,
    refetch,
  } = useFetchAll<CompanyData_V1.Company>(
    [COMPANIES_QUERY_KEY, pageSize, companyNames, ssoDomain, includeBinaries],
    (page, size) =>
      companyDataService.getCompanies(
        page,
        size,
        companyNames,
        ssoDomain,
        includeBinaries,
      ),
    {
      enabled,
      pageSize,
      staleTime: 10 * 60 * 1000,
      gcTime: GC_TIME_MS,
    },
  );

  const companyNamesByIdMap = useMemo(() => {
    if (!companiesData) {
      return {};
    }

    return companiesData.reduce<Record<string, string>>((acc, company) => {
      acc[company.companyId] = company.friendlyName;

      return acc;
    }, {});
  }, [companiesData]);

  return {
    data: companiesData,
    companiesData,
    isLoading: companiesLoading,
    isFetching,
    companiesLoading,
    error,
    refetch: async () => {
      await refetch();
    },
    companyNamesByIdMap,
  };
};
